import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';
import Multiplier from '@/core/types/Multiplier';
import Title_ru_RU_Class from '@/core/misc/decorators/Title_ru_RU_Class';

/**
 * Ставка НДС в ПРОЦЕНТАХ: 20 означает 20%. Точность — 0 знаков.
 *
 * Хранится процентом, а не множителем (в отличие от Multiplier, где те же
 * 20% это 1.20): с бека ставка приходит процентом и пользователю показывается
 * процентом, промежуточное преобразование в обе стороны только добавляло бы
 * потери на округлении.
 *
 * SCALE = 0 — дробных ставок в поддерживаемых юрисдикциях нет (РФ 0/10/20,
 * УЗ 0/12). Если появятся, поднимать scale здесь, а не заводить второй тип.
 *
 * Живёт и как расчётный тип, и как элемент справочника Dictionaries.vatRates:
 * отсюда геттеры id и name, которых нет у остальных Numeric. Отдельный
 * словарный класс не нужен — ставка одна и та же сущность в обеих ролях.
 *
 * Зеркальный бекенд: core/types/VatRate.php на BcMath\Number.
 */
@Title_ru_RU_Class('Ставка НДС')
class VatRate extends Numeric {
    protected static readonly SCALE = 0;

    /**
     * С бека ставка приходит числом, а не строкой (в отличие от денежных
     * типов): процент целый, потери точности при передаче числом нет.
     */
    static fromServer(value: Decimal.Value | null | undefined): VatRate {
        return new VatRate(VatRate.toDecimal(value));
    }

    /** Результат вычисления — уже посчитанный Decimal. */
    static fromOperation(value: Decimal): VatRate {
        return new VatRate(value);
    }

    /**
     * Сериализация: ставка, которой нет, уходит на бек как null.
     *
     * Отличие от базового toJSON, который всегда печатает число. Ноль и
     * пустое значение означают здесь одно и то же — налога нет, — и база
     * проверяет именно null. Приводим на границе типа, чтобы каждый домен
     * не выяснял это заново: сегодня ноль в данных это либо инициализация
     * типа, которая срабатывает всегда, либо введённое руками «без НДС».
     *
     * Ноль как действующая ставка не поддерживается сознательно. В РФ 0% и
     * «без НДС» — разные вещи, и если экспорт по нулевой ставке когда-нибудь
     * понадобится, разводить их придётся отдельным признаком, а не нулём в
     * этом поле; на нуль тогда уже завязана база.
     *
     * Зеркало на беке — VatRate::jsonSerialize, оно должно отдавать null на
     * тех же условиях, иначе стороны разойдутся.
     */
    toJSON(): string | null {
        if (this.isPositive() === true) {
            return this.value.toFixed(this.scale);
        }
        else {
            return null;
        }
    }

    /**
     * Идентификатор для справочника и селектов — сам процент.
     * Отдельного id у ставки нет и быть не может: две ставки по 20% — одна
     * и та же ставка.
     */
    get id(): number {
        return this.value.toNumber();
    }

    /** Отображаемое имя для селектов, например «20%». */
    get name(): string {
        return this.format();
    }
    /** Отображаемое имя для VSelectV2, например «20%». */
    toString(): string {
        return this.format()
    }

    /**
     * Ставка как множитель: 20% → 1.20.
     * Мост к операциям над ценой и суммой, которые работают множителями, —
     * чтобы ради бизнес-арифметики не выходить в native number через toNumber.
     */
    asMultiplier(): Multiplier {
        return Multiplier.fromPercent(this.value);
    }

    /** Ставка показывается процентом со знаком: "20%".
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN". */
    format(): string {
        if (this.isEmpty()) {
            return '';
        }

        return `${this.value.toFixed(0)}%`;
    }
}

export default VatRate;